/**
 * Cookie Policy Page — Legacy Triangle
 */

import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Cookie, Mail, Phone } from "lucide-react";

export default function CookiePolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0f2d52]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0f2d52] via-[#1a3a5c] to-[#0f2d52]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#48d480]/10 border border-[#48d480]/30 rounded-full px-4 py-2 mb-6">
            <Cookie className="w-4 h-4 text-[#48d480]" />
            <span className="text-[#48d480] text-sm font-medium">Cookie Information</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Cookie Policy
          </h1>
          <p className="text-white/70 text-lg">Last updated: March 18, 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none" style={{ fontFamily: "'Inter', sans-serif" }}>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">1. What Are Cookies?</h2>
              <p className="text-gray-600 leading-relaxed">
                Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and give website owners information about how their site is being used. Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your device after you close your browser, while session cookies are deleted when you close your browser.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">2. How We Use Cookies</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Legacy Triangle uses cookies and similar technologies for several purposes:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Essential Cookies:</strong> These cookies are necessary for the website to function properly and cannot be switched off. They are usually set in response to actions you take, such as setting your privacy preferences, logging in, or filling out forms.</li>
                <li><strong>Performance Cookies:</strong> These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us understand which pages are the most and least popular and how visitors move around the site.</li>
                <li><strong>Functional Cookies:</strong> These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.</li>
                <li><strong>Targeting/Advertising Cookies:</strong> These cookies may be set through our site by our advertising partners to build a profile of your interests and show you relevant advertisements on other sites.</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">3. Types of Cookies We Use</h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-bold text-[#0f2d52] mb-3">Essential Cookies</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-[#0f2d52]">Cookie Name</th>
                      <th className="text-left py-2 text-[#0f2d52]">Purpose</th>
                      <th className="text-left py-2 text-[#0f2d52]">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr className="border-b border-gray-100">
                      <td className="py-2">session_id</td>
                      <td className="py-2">Maintains user session</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">cookie_consent</td>
                      <td className="py-2">Stores cookie preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="py-2">csrf_token</td>
                      <td className="py-2">Security token</td>
                      <td className="py-2">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-bold text-[#0f2d52] mb-3">Performance Cookies</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-[#0f2d52]">Cookie Name</th>
                      <th className="text-left py-2 text-[#0f2d52]">Purpose</th>
                      <th className="text-left py-2 text-[#0f2d52]">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr className="border-b border-gray-100">
                      <td className="py-2">_ga</td>
                      <td className="py-2">Google Analytics tracking</td>
                      <td className="py-2">2 years</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">_gid</td>
                      <td className="py-2">Google Analytics user distinction</td>
                      <td className="py-2">24 hours</td>
                    </tr>
                    <tr>
                      <td className="py-2">_gat</td>
                      <td className="py-2">Google Analytics throttling</td>
                      <td className="py-2">1 minute</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-[#0f2d52] mb-3">Functional Cookies</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-[#0f2d52]">Cookie Name</th>
                      <th className="text-left py-2 text-[#0f2d52]">Purpose</th>
                      <th className="text-left py-2 text-[#0f2d52]">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr className="border-b border-gray-100">
                      <td className="py-2">user_preferences</td>
                      <td className="py-2">Stores user preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="py-2">chat_session</td>
                      <td className="py-2">Maintains chat history</td>
                      <td className="py-2">30 days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">4. Third-Party Cookies</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                In addition to our own cookies, we may also use various third-party cookies to report usage statistics, deliver advertisements, and so forth. These may include:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Google Analytics:</strong> Used to analyze website traffic and user behavior</li>
                <li><strong>Facebook Pixel:</strong> Used for advertising and retargeting</li>
                <li><strong>LinkedIn Insight Tag:</strong> Used for conversion tracking and retargeting</li>
                <li><strong>HubSpot:</strong> Used for marketing automation and analytics</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">5. Managing Your Cookie Preferences</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                You have the right to decide whether to accept or reject cookies. You can manage your cookie preferences in several ways:
              </p>
              
              <h3 className="text-xl font-semibold text-[#0f2d52] mb-3">Browser Settings</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Most web browsers allow you to control cookies through their settings. You can set your browser to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                <li>Block all cookies</li>
                <li>Accept only first-party cookies</li>
                <li>Delete cookies when you close your browser</li>
                <li>Alert you when cookies are being set</li>
              </ul>
              
              <p className="text-gray-600 leading-relaxed mb-4">
                Here are links to cookie management instructions for common browsers:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><a href="https://support.google.com/chrome/answer/95647" className="text-[#48d480] hover:underline" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" className="text-[#48d480] hover:underline" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" className="text-[#48d480] hover:underline" target="_blank" rel="noopener noreferrer">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" className="text-[#48d480] hover:underline" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">6. Impact of Disabling Cookies</h2>
              <p className="text-gray-600 leading-relaxed">
                Please note that if you choose to block or delete cookies, some features of our website may not function properly. Essential cookies are required for the website to operate, and disabling them may prevent you from using certain features such as filling out forms or using the chat assistant.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">7. Do Not Track Signals</h2>
              <p className="text-gray-600 leading-relaxed">
                Some browsers have a "Do Not Track" feature that signals to websites that you do not want to have your online activity tracked. Our website currently does not respond to "Do Not Track" signals, as there is no industry standard for how to respond to such signals.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">8. Updates to This Policy</h2>
              <p className="text-gray-600 leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Cookie Policy on this page and updating the "Last updated" date.
              </p>
            </div>

            <div className="bg-[#0f2d52]/5 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">9. Contact Us</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <div className="space-y-3">
                <a href="mailto:info@legacytriangle.com" className="flex items-center gap-3 text-[#0f2d52] hover:text-[#48d480] transition-colors">
                  <Mail className="w-5 h-5 text-[#48d480]" />
                  info@legacytriangle.com
                </a>
                <a href="tel:+19844644672" className="flex items-center gap-3 text-[#0f2d52] hover:text-[#48d480] transition-colors">
                  <Phone className="w-5 h-5 text-[#48d480]" />
                  (984) 464-4672
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
